import { useCallback, useRef } from 'react';
import cn from 'classnames';

import Button from 'components/form/Button.component';
import DropdownFoldout from 'components/generic/DropdownFoldout.component';
import CurrencySign from 'components/price/CurrencySign.component';
import S from 'constants/string.const';
import useOnClickOutside from 'hooks/useOnClickOutsideRef.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';

import CN from './DepositPanel.module.scss';

const DepositPanel = ({ tag, text, price, variant, status, children, onDepositButtonClick }) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const { val: isDropdownRendered, tip: toggleDropdown, nay: hideDropdown } = useTip(false);

  const wrapperRef = useRef(null);
  useOnClickOutside(wrapperRef, hideDropdown);

  const isNotDeclined = variant !== 'declined';
  const isDeclined = variant === 'declined';
  const isMissing = status === 'missing';

  const handleDepositClick = useCallback(() => {
    if (onDepositButtonClick?.() === true) return;
    toggleDropdown();
  }, [onDepositButtonClick, toggleDropdown]);

  return (
    <div
      data-bem="DepositPanel"
      ref={wrapperRef}
      data-tag={fromAnyToTrimmed(tag)}
      data-is-missing={String(isMissing)}
      data-is-declined={String(isDeclined)}
      data-dep-status={String(status)}
      data-dep-price={String(price)}
      data-variant={String(variant)}
      className={cn({
        [CN.component]: true,
        [CN[`${variant}Variant`]]: variant, // noDeposit | paid | requested | refunded | declined
        [CN.dark]: isDarkMode,
      })}
    >
      <div data-bem="DepositPanel__text" className={CN.text}>
        <div className={CN.icon}> {isDeclined ? S.crossMark : S.moneyBag}</div>
        {text}
      </div>

      {isNotDeclined &&
        (isMissing ? (
          <div data-bem="DepositPanel__button" className={CN.buttonContainer}>
            <Button tag="DepositPanel__button" data-testid="DepositPanel__button" variant="link" className={CN.button} onClick={handleDepositClick}>
              {t('composite.depositPanel.depositButton')}
            </Button>
            {isDropdownRendered && (
              <DropdownFoldout tag="DepositPanel__menu" width="sm" position="depositPanel">
                <DropdownFoldout.Body>{children}</DropdownFoldout.Body>
              </DropdownFoldout>
            )}
          </div>
        ) : (
          fromAnyToBool(price) && (
            <div data-bem="DepositPanel__price">
              {price < 0 && '-'}
              <CurrencySign />
              {Math.abs(price)}
            </div>
          )
        ))}
    </div>
  );
};

export default DepositPanel;
